/**
 * PagosSection — Tab "Pagos / Configuración" en Ajustes (rc19.1+).
 *
 * rc19.1: simplificada para mostrar SOLO configuración global Toku:
 *   - Toggle activa
 *   - Ambiente (sandbox / producción)
 *   - API Key (org-level)
 *   - Webhook Secret (org-wide, único endpoint)
 *   - URL receptor webhook
 *   - "Probar conexión" — usa creds de la primera empresa activa
 *
 * Las CUENTAS DE COBRO (account_id + account_key por ambiente) viven en
 * cada empresa, configurables en Ajustes/Empresas → Editar. Cada cliente
 * pertenece a una empresa y sus cobros se enrutan a la cuenta Toku de
 * esa empresa automáticamente.
 *
 * Solo visible para usuarios con permiso `integrations.payments.configure`.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  CreditCard, CheckCircle2, XCircle, Save, Loader2, Copy, TestTube2,
  AlertTriangle, ExternalLink, KeyRound, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiCall, type ApiError } from "@/lib/api";

/* ─── Tipos (rc19.1: account_* removidos) ─────────────────────── */
type EnvCreds = {
  api_key_masked: string | null;
  api_key_has_value: boolean;
  webhook_secret_masked: string | null;
  webhook_secret_has_value: boolean;
};

type TokuConfig = {
  enabled: boolean;
  environment: "sandbox" | "production";
  verificar_webhook?: boolean;
  base_url: string;
  webhook_receiver_url: string;
  dominio_publico?: string | null;
  dominio_install?: string | null;
  sandbox: EnvCreds;
  production: EnvCreds;
};

type EnvCredsPatch = {
  api_key?: string | null;
  webhook_secret?: string | null;
};

type TokuConfigPatch = {
  enabled?: boolean;
  verificar_webhook?: boolean;
  environment?: "sandbox" | "production";
  sandbox?: EnvCredsPatch;
  production?: EnvCredsPatch;
};

type TestResponse = {
  success: boolean;
  environment: string;
  empresa_probada?: string;
  message?: string;
  error?: string;
  raw?: unknown;
};

/* ─── Hooks ─────────────────────────────────────────────────── */
function useTokuConfig() {
  return useQuery<TokuConfig>({
    queryKey: ["toku-config"],
    queryFn: () => apiCall<TokuConfig>("get", "/api/v1/integrations/payments/toku/config"),
    staleTime: 10_000,
  });
}

/* ─── Componente ─────────────────────────────────────────────── */
export function PagosSection() {
  const qc = useQueryClient();
  const { data: config, isLoading, isError } = useTokuConfig();

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [sbApiKey, setSbApiKey] = useState("");
  const [sbWebhookSecret, setSbWebhookSecret] = useState("");
  const [prodApiKey, setProdApiKey] = useState("");
  const [prodWebhookSecret, setProdWebhookSecret] = useState("");
  const [dirty, setDirty] = useState(false);

  const [lastTest, setLastTest] = useState<TestResponse | null>(null);
  // rc129 — override del dominio público (sistema.dominio_publico)
  const [dominio, setDominio] = useState("");
  // rc131 — confirmar pagos de webhook con GET a Toku antes de aplicarlos
  const verificar = config?.verificar_webhook ?? true;
  const verifMut = useMutation({
    mutationFn: (value: boolean) =>
      apiCall<TokuConfig>("patch", "/api/v1/integrations/payments/toku/config", { json: { verificar_webhook: value } }),
    onSuccess: (_d, value) => {
      qc.invalidateQueries({ queryKey: ["toku-config"] });
      toast.success(value ? "Verificación con Toku activada" : "Verificación con Toku desactivada");
    },
    onError: (e: ApiError) => toast.error(e.message || "No se pudo cambiar"),
  });

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setEnvironment(config.environment);
      setSbApiKey(""); setSbWebhookSecret("");
      setProdApiKey(""); setProdWebhookSecret("");
      setDirty(false);
      setDominio(config.dominio_publico ?? "");
    }
  }, [config]);

  const dominioMut = useMutation({
    mutationFn: (value: string) =>
      apiCall("put", "/api/v1/config/sistema.dominio_publico", {
        json: { value: value.trim(), value_type: "string", grupo: "sistema" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["toku-config"] });
      toast.success("Dominio público guardado");
    },
    onError: (e: ApiError) => toast.error(e.message || "Error al guardar el dominio"),
  });

  const patchMut = useMutation({
    mutationFn: (body: TokuConfigPatch) =>
      apiCall<TokuConfig>("patch", "/api/v1/integrations/payments/toku/config", { json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["toku-config"] });
      toast.success("Configuración guardada");
      setDirty(false);
    },
    onError: (e: ApiError) => toast.error(e.detail || "Error al guardar"),
  });

  const testMut = useMutation({
    mutationFn: (body: { environment?: "sandbox" | "production" }) =>
      apiCall<TestResponse>("post", "/api/v1/integrations/payments/toku/test-connection", { json: body }),
    onSuccess: (data) => {
      setLastTest(data);
      if (data.success) {
        toast.success(data.message || "Conexión OK");
      } else {
        toast.error(data.error || "Falló la prueba");
      }
    },
    onError: (e: ApiError) => {
      setLastTest({ success: false, environment, error: e.detail });
      toast.error(e.detail || "Error en prueba");
    },
  });

  function handleToggle() {
    setEnabled(!enabled);
    setDirty(true);
  }

  function buildPatchBody(): TokuConfigPatch {
    const body: TokuConfigPatch = { enabled, environment };
    const sb: EnvCredsPatch = {};
    if (sbApiKey) sb.api_key = sbApiKey;
    if (sbWebhookSecret) sb.webhook_secret = sbWebhookSecret;
    if (Object.keys(sb).length > 0) body.sandbox = sb;

    const pr: EnvCredsPatch = {};
    if (prodApiKey) pr.api_key = prodApiKey;
    if (prodWebhookSecret) pr.webhook_secret = prodWebhookSecret;
    if (Object.keys(pr).length > 0) body.production = pr;

    return body;
  }

  function handleSave() {
    patchMut.mutate(buildPatchBody());
  }

  function handleTest() {
    setLastTest(null);
    testMut.mutate({ environment });
  }

  function handleCopyWebhookUrl() {
    if (!config?.webhook_receiver_url) return;
    navigator.clipboard.writeText(config.webhook_receiver_url);
    toast.success("URL copiada al portapapeles");
  }

  if (isLoading) return <div className="text-xs text-muted-foreground">Cargando configuración…</div>;
  if (isError || !config) return <div className="text-xs text-destructive">Error cargando configuración Toku</div>;

  const currentCreds = environment === "sandbox" ? config.sandbox : config.production;
  const credsOk = currentCreds.api_key_has_value;  // mínimo para Toku org-wide

  const fullyActive = enabled && credsOk;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Card: Estado de la pasarela ───────────────────────── */}
      <div className="rounded border bg-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <CreditCard size={20} className={fullyActive ? "text-green-500" : "text-muted-foreground"} />
            <div>
              <h3 className="text-sm font-semibold">Toku — Pasarela de pago</h3>
              <p className="text-xs text-muted-foreground">
                {fullyActive
                  ? `Activa en ${environment === "sandbox" ? "Sandbox" : "Producción"}`
                  : enabled
                    ? "Activada pero faltan credenciales globales"
                    : "Pasarela desactivada"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              enabled ? "bg-green-500" : "bg-muted"
            }`}
            role="switch"
            aria-checked={enabled}
          >
            <span
              className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform ${
                enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        {!enabled && (
          <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
            <div className="flex gap-2">
              <AlertTriangle size={14} className="shrink-0 text-amber-500" />
              <div className="text-muted-foreground">
                Mientras está OFF: las facturas NO se sincronizan, los pagos en efectivo
                NO se notifican, los webhooks de Toku se guardan pero NO se procesan.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Card: Hint sobre cuentas de cobro per-empresa ──────── */}
      <div className="rounded border border-primary/30 bg-primary/5 p-3">
        <div className="flex items-start gap-2">
          <Building2 size={14} className="mt-0.5 shrink-0 text-primary" />
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Las cuentas de cobro (Account ID + Account Key) se configuran por empresa.</span>
            {" "}Esta sección solo gestiona los datos globales de la organización Toku (API Key + Webhook).
            Para configurar las cuentas de cobro de cada sub-empresa, ve a{" "}
            <Link to="/ajustes/empresas" className="font-medium text-primary underline">Ajustes / Empresas</Link>.
          </div>
        </div>
      </div>

      {/* ── Card: Ambiente ────────────────────────────────────── */}
      <div className="rounded border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Ambiente activo</h3>
        <div className="flex gap-3">
          <label className={`flex flex-1 cursor-pointer items-center gap-2 rounded border p-3 ${
            environment === "sandbox" ? "border-primary bg-primary/5" : "border-border"
          }`}>
            <input
              type="radio"
              name="env"
              checked={environment === "sandbox"}
              onChange={() => { setEnvironment("sandbox"); setDirty(true); }}
              className="h-4 w-4"
            />
            <div>
              <div className="text-sm font-medium">Sandbox</div>
              <div className="text-xs text-muted-foreground">Ambiente de pruebas</div>
            </div>
          </label>
          <label className={`flex flex-1 cursor-pointer items-center gap-2 rounded border p-3 ${
            environment === "production" ? "border-primary bg-primary/5" : "border-border"
          }`}>
            <input
              type="radio"
              name="env"
              checked={environment === "production"}
              onChange={() => { setEnvironment("production"); setDirty(true); }}
              className="h-4 w-4"
            />
            <div>
              <div className="text-sm font-medium">Producción</div>
              <div className="text-xs text-muted-foreground">Cobra dinero real</div>
            </div>
          </label>
        </div>
      </div>

      {/* ── Card: Credenciales globales del ambiente ─────────── */}
      <div className="rounded border bg-card p-4">
        <h3 className="mb-1 text-sm font-semibold">
          Credenciales globales — {environment === "sandbox" ? "Sandbox" : "Producción"}
        </h3>
        <p className="mb-3 text-xs text-muted-foreground">
          API Key (organization-level) y Webhook Secret (único endpoint webhook).
          Los inputs vacíos NO modifican los valores guardados.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs">
              API Key
              {currentCreds.api_key_has_value && (
                <span className="ml-2 text-muted-foreground">actual: {currentCreds.api_key_masked}</span>
              )}
            </Label>
            <Input
              type="password"
              value={environment === "sandbox" ? sbApiKey : prodApiKey}
              onChange={(e) => {
                if (environment === "sandbox") setSbApiKey(e.target.value);
                else setProdApiKey(e.target.value);
                setDirty(true);
              }}
              placeholder={currentCreds.api_key_has_value ? "(deja vacío para no cambiar)" : "Pega tu API Key"}
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <KeyRound size={11} />
              Webhook Secret
              {currentCreds.webhook_secret_has_value && (
                <span className="ml-2 text-muted-foreground">actual: {currentCreds.webhook_secret_masked}</span>
              )}
            </Label>
            <Input
              type="password"
              value={environment === "sandbox" ? sbWebhookSecret : prodWebhookSecret}
              onChange={(e) => {
                if (environment === "sandbox") setSbWebhookSecret(e.target.value);
                else setProdWebhookSecret(e.target.value);
                setDirty(true);
              }}
              placeholder={currentCreds.webhook_secret_has_value ? "(deja vacío para no cambiar)" : "whesec_xxx (lo obtienes al registrar endpoint en Toku)"}
              className="font-mono text-xs"
            />
          </div>
        </div>
      </div>

      {/* ── Card: Verificación de webhooks (rc131) ─────────────── */}
      <div className="rounded border bg-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold">Confirmar cada pago con Toku antes de aplicarlo</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              La firma del webhook no cubre el cuerpo del mensaje. Con esto activo, antes de
              registrar un pago ByteISP consulta la transacción en Toku (solo lectura) y usa
              lo que Toku confirma: monto, factura, cliente y estado. Si no coincide, el pago
              NO se aplica y aparece como excepción crítica. Apágalo solo si Toku te lo pide
              por un problema de su API.
            </p>
          </div>
          <button
            type="button"
            onClick={() => verifMut.mutate(!verificar)}
            disabled={verifMut.isPending}
            className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              verificar ? "bg-green-500" : "bg-muted"
            }`}
            role="switch"
            aria-checked={verificar}
          >
            <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform ${
              verificar ? "translate-x-5" : "translate-x-0"
            }`} />
          </button>
        </div>
        {!verificar && (
          <div className="mt-3 rounded border border-red-500/30 bg-red-500/5 p-2 text-xs text-muted-foreground">
            Verificación APAGADA: los pagos se aplican confiando solo en la firma del webhook.
          </div>
        )}
      </div>

      {/* ── Card: Webhook URL ─────────────────────────────────── */}
      <div className="rounded border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold">URL del receptor webhook</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Registra esta URL en el panel de Toku (Webhooks → Crear endpoint). Toku envía aquí
          los eventos de pagos, anulaciones, etc. de TODAS las cuentas de cobro
          (org-wide). El receptor valida la firma HMAC contra el webhook secret
          configurado arriba.
        </p>
        <div className="flex items-center gap-2">
          <Input
            value={config.webhook_receiver_url}
            readOnly
            className="font-mono text-xs"
          />
          <Button type="button" variant="outline" size="sm" onClick={handleCopyWebhookUrl}>
            <Copy size={14} />
          </Button>
        </div>
        {/* rc129 — dominio público: por default el del install (DOMAIN_ADMIN) */}
        <div className="mt-3 border-t pt-3">
          <Label className="text-xs">Dominio público (opcional)</Label>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Si se deja vacío, la URL usa el dominio del servidor
            {config.dominio_install ? <> (<span className="font-mono">{config.dominio_install}</span>)</> : null}.
            Escribe otro solo si Toku debe llamar a un dominio distinto.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={dominio}
              onChange={(e) => setDominio(e.target.value)}
              placeholder={config.dominio_install ?? "mi-dominio.com"}
              className="font-mono text-xs"
            />
            <Button
              type="button" variant="outline" size="sm"
              disabled={dominioMut.isPending || (dominio.trim() === (config.dominio_publico ?? ""))}
              onClick={() => dominioMut.mutate(dominio)}
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>

      {/* ── Resultado del último test ─────────────────────────── */}
      {lastTest && (
        <div className={`rounded border p-3 text-xs ${
          lastTest.success ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"
        }`}>
          <div className="flex items-start gap-2">
            {lastTest.success
              ? <CheckCircle2 size={16} className="shrink-0 text-green-500" />
              : <XCircle size={16} className="shrink-0 text-red-500" />}
            <div className="flex-1">
              <div className="font-medium">
                {lastTest.success ? "Conexión OK" : "Falló la prueba"}
                <span className="ml-2 text-muted-foreground">({lastTest.environment})</span>
                {lastTest.empresa_probada && (
                  <span className="ml-2 text-muted-foreground">— empresa: {lastTest.empresa_probada}</span>
                )}
              </div>
              {lastTest.error && <div className="mt-1 text-muted-foreground">{lastTest.error}</div>}
              {lastTest.message && <div className="mt-1 text-muted-foreground">{lastTest.message}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Acciones ──────────────────────────────────────────── */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={testMut.isPending || !credsOk}
        >
          {testMut.isPending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <TestTube2 size={14} className="mr-1" />}
          Probar conexión
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!dirty || patchMut.isPending}
        >
          {patchMut.isPending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
          Guardar cambios
        </Button>
      </div>

      <div className="text-[11px] text-muted-foreground">
        Documentación oficial Toku: <a href="https://toku.readme.io/" target="_blank" rel="noreferrer" className="underline">toku.readme.io</a> <ExternalLink size={10} className="inline ml-0.5" />
      </div>
    </div>
  );
}
